import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InspectorControls, MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { PanelBody, TextControl, TextareaControl, Button, ButtonGroup, Icon } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { arrowUp, arrowDown, trash } from '@wordpress/icons';

registerBlockType('puroclean/who-we-serve', {
    edit({ attributes, setAttributes }) {
        const { title, clients, imageUrl, imageAlt } = attributes;
        
        const blockProps = useBlockProps({
            className: 'who-we-serve'
        });

        const updateClient = (index, field, value) => {
            const newClients = [...clients];
            newClients[index] = { ...newClients[index], [field]: value };
            setAttributes({ clients: newClients });
        };

        const addClient = () => {
            const newClients = [...clients, {
                title: 'New Client',
                description: 'Description for new client',
                url: '/clients#new-client',
                bgColor: 'bg-primary',
                iconUrl: ''
            }];
            setAttributes({ clients: newClients });
        };

        const removeClient = (index) => {
            const newClients = clients.filter((_, i) => i !== index);
            setAttributes({ clients: newClients });
        };

        const moveClientUp = (index) => {
            if (index > 0) {
                const newClients = [...clients];
                [newClients[index - 1], newClients[index]] = [newClients[index], newClients[index - 1]];
                setAttributes({ clients: newClients });
            }
        };

        const moveClientDown = (index) => {
            if (index < clients.length - 1) {
                const newClients = [...clients];
                [newClients[index], newClients[index + 1]] = [newClients[index + 1], newClients[index]];
                setAttributes({ clients: newClients });
            }
        };

        return (
            <>
                <InspectorControls>
                    <PanelBody title={__('Content Settings', 'puroclean')}>
                        <TextControl
                            label={__('Section Title', 'puroclean')}
                            value={title}
                            onChange={(value) => setAttributes({ title: value })}
                        />
                        
                        <div className="mt-6">
                            <label className="text-sm font-medium mb-2 block">{__('Background Image', 'puroclean')}</label>
                            <MediaUploadCheck>
                                <MediaUpload
                                    onSelect={(media) => {
                                        setAttributes({
                                            imageUrl: media.url,
                                            imageAlt: media.alt || 'Background Image'
                                        });
                                    }}
                                    allowedTypes={['image']}
                                    value={imageUrl}
                                    render={({ open }) => (
                                        <div className="space-y-2">
                                            {imageUrl && (
                                                <div className="relative">
                                                    <img 
                                                        src={imageUrl} 
                                                        alt={imageAlt}
                                                        className="w-full h-32 object-cover rounded border"
                                                    />
                                                    <Button
                                                        variant="secondary"
                                                        size="small"
                                                        onClick={() => setAttributes({ imageUrl: '', imageAlt: '' })}
                                                        className="absolute top-2 right-2"
                                                        isDestructive
                                                    >
                                                        {__('Remove', 'puroclean')}
                                                    </Button>
                                                </div>
                                            )}
                                            <Button
                                                variant={imageUrl ? 'secondary' : 'primary'}
                                                onClick={open}
                                                size="small"
                                            >
                                                {imageUrl ? __('Change Image', 'puroclean') : __('Select Image', 'puroclean')}
                                            </Button>
                                        </div>
                                    )}
                                />
                            </MediaUploadCheck>
                            
                            <TextControl
                                label={__('Image Alt Text', 'puroclean')}
                                value={imageAlt}
                                onChange={(value) => setAttributes({ imageAlt: value })}
                                help={__('Describe the image for accessibility', 'puroclean')}
                            />
                        </div>
                        
                        <div className="mt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-sm font-medium">{__('Client Segments', 'puroclean')}</h3>
                                <Button 
                                    variant="primary" 
                                    size="small"
                                    onClick={addClient}
                                >
                                    {__('Add Client', 'puroclean')}
                                </Button>
                            </div>
                            
                            {clients.map((client, index) => (
                                <div key={index} className="border border-gray-200 rounded p-4 mb-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-sm font-medium">{__('Client', 'puroclean')} {index + 1}</h4>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="secondary"
                                                    size="small"
                                                    onClick={() => moveClientUp(index)}
                                                    disabled={index === 0}
                                                    icon={arrowUp}
                                                    label={__('Move Up', 'puroclean')}
                                                />
                                                <Button
                                                    variant="secondary"
                                                    size="small"
                                                    onClick={() => moveClientDown(index)}
                                                    disabled={index === clients.length - 1}
                                                    icon={arrowDown}
                                                    label={__('Move Down', 'puroclean')}
                                                />
                                            </div>
                                        </div>
                                        <Button 
                                            variant="secondary" 
                                            size="small"
                                            onClick={() => removeClient(index)}
                                            isDestructive
                                            icon={trash}
                                            label={__('Remove', 'puroclean')}
                                        />
                                    </div>
                                    
                                    <TextControl
                                        label={__('Title', 'puroclean')}
                                        value={client.title}
                                        onChange={(value) => updateClient(index, 'title', value)}
                                    />
                                    
                                    <TextareaControl
                                        label={__('Description', 'puroclean')}
                                        value={client.description}
                                        onChange={(value) => updateClient(index, 'description', value)}
                                        rows={3}
                                    />
                                    
                                    <TextControl
                                        label={__('Link URL', 'puroclean')}
                                        value={client.url}
                                        onChange={(value) => updateClient(index, 'url', value)}
                                    />
                                    
                                    <div className="mt-4">
                                        <label className="text-sm font-medium mb-2 block">{__('Icon', 'puroclean')}</label>
                                        <MediaUploadCheck>
                                            <MediaUpload
                                                onSelect={(media) => {
                                                    updateClient(index, 'iconUrl', media.url);
                                                }}
                                                allowedTypes={['image']}
                                                value={client.iconUrl}
                                                render={({ open }) => (
                                                    <div className="space-y-2">
                                                        {client.iconUrl && (
                                                            <div className="relative">
                                                                <img 
                                                                    src={client.iconUrl} 
                                                                    alt={`${client.title} Icon`}
                                                                    className="w-12 h-12 object-contain rounded border"
                                                                />
                                                                <Button
                                                                    variant="secondary"
                                                                    size="small"
                                                                    onClick={() => updateClient(index, 'iconUrl', '')}
                                                                    className="absolute -top-2 -right-2"
                                                                    isDestructive
                                                                    icon={trash}
                                                                    label={__('Remove Icon', 'puroclean')}
                                                                />
                                                            </div>
                                                        )}
                                                        <Button
                                                            variant={client.iconUrl ? 'secondary' : 'primary'}
                                                            onClick={open}
                                                            size="small"
                                                        >
                                                            {client.iconUrl ? __('Change Icon', 'puroclean') : __('Select Icon', 'puroclean')}
                                                        </Button>
                                                    </div>
                                                )}
                                            />
                                        </MediaUploadCheck>
                                    </div>
                                    
                                    <div className="mt-2">
                                        <label className="text-sm font-medium mb-2 block">{__('Background Color', 'puroclean')}</label>
                                        <ButtonGroup>
                                            <Button
                                                variant={client.bgColor === 'bg-primary' ? 'primary' : 'secondary'}
                                                onClick={() => updateClient(index, 'bgColor', 'bg-primary')}
                                                size="small"
                                            >
                                                {__('Primary', 'puroclean')}
                                            </Button>
                                            <Button
                                                variant={client.bgColor === 'bg-secondary' ? 'primary' : 'secondary'}
                                                onClick={() => updateClient(index, 'bgColor', 'bg-secondary')}
                                                size="small"
                                            >
                                                {__('Secondary', 'puroclean')}
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </PanelBody>
                </InspectorControls>
                
                <div {...blockProps}>
                    <div className="py-16 bg-[#F6F6F6]">
                        <div className="max-w-content mx-auto">
                            <div className="flex items-stretch gap-0">
                                {/* Left Column - Image (45%) */}
                                <div className="w-[45%] h-auto min-h-[400px] flex">
                                    <img 
                                        src={imageUrl || "/wp-content/themes/puroclean-tw/assets/img/purovan@2x.png"} 
                                        alt={imageAlt || "PuroClean Service Van"} 
                                        className="w-full h-full object-cover rounded-l-[10px]"
                                    />
                                </div>
                                
                                {/* Right Column - Content (55%) */}
                                <div className="w-[55%]">
                                    <div className="bg-white shadow-[0px_0px_20px_rgba(0,0,0,0.06)] rounded-r-[10px] rounded-l-none p-12 h-full">
                                        <h2 className="text-3xl font-bold mb-10">{title}</h2>
                                        
                                        <div className="grid grid-cols-2 gap-8">
                                            {clients.map((client, index) => {
                                                const iconMap = {
                                                    'Residential': '/wp-content/themes/puroclean-tw/assets/img/icons/house.png',
                                                    'Property Managers': '/wp-content/themes/puroclean-tw/assets/img/icons/keys.png',
                                                    'Commercial': '/wp-content/themes/puroclean-tw/assets/img/icons/building.png',
                                                    'Insurance Agents': '/wp-content/themes/puroclean-tw/assets/img/icons/security.png'
                                                };
                                                
                                                const iconSrc = client.iconUrl || iconMap[client.title] || '/wp-content/themes/puroclean-tw/assets/img/icons/house.png';
                                                
                                                return (
                                                    <div key={index} className="flex items-center">
                                                        <div className="mr-6 flex-shrink-0">
                                                            <div className={`w-[77px] h-[77px] rounded-full ${client.bgColor} flex items-center justify-center`}>
                                                                <img 
                                                                    src={iconSrc} 
                                                                    alt={`${client.title} Icon`} 
                                                                    width="40" 
                                                                    height="40"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h3 className="font-bold text-lg mb-1">{client.title}</h3>
                                                            <p className={client.title === 'Residential' ? '!leading-[24px]' : ''}>{client.description}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    },
    save() {
        return null; // Server-side rendered
    }
}); 